import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import styled from 'styled-components';

import Loading from '../../../components/Loading';
import { Container } from '../../../styles/layout/Container';
import {
   PageHeader,
   PageHeaderActions,
   PageHeaderText,
   PageTitle,
   PageSubtitle,
} from '../../../styles/layout/PageHeader';
import {
   Section,
   SectionHeader,
   SectionBody,
   SectionTitle,
   SectionActions,
} from '../../../styles/layout/Section';
import { Table } from '../../../styles/table/Table';
import { TableWrapper } from '../../../styles/table/TableWrapper';
import { PrimaryButton, SmallButton } from '../../../styles/ui/Button';

import {
   listServiceCatalog,
   createServiceType,
   updateServiceType,
   updateServiceTypeCommission,
} from '../../../services/routing/routingApi';
import ServiceTypeFormModal from './components/ServiceTypeFormModal';

// =============================================================================
// Catálogo de serviços (handoff 0039) — fonte UNIFICADA dos ~35 serviços
// (technician_commission_table). Dois donos por permissão:
//   - LOGÍSTICA (routing:service_catalog:manage): cadastro, tempo de execução
//     (min/un, alimenta estimated_duration_minutes dos itens) e ciclo de vida.
//   - FINANCEIRO (finance:commission:manage): valor de comissão por unidade.
// Leitura: routing:schedule_item:read. "Tirar de uso" = toggle is_active.
// =============================================================================

const InlineCheckbox = styled.label`
   display: inline-flex;
   align-items: center;
   gap: 6px;
   font-size: 0.85rem;
   cursor: pointer;
   user-select: none;
   color: ${({ theme }) => theme?.colors?.textSecondary ?? '#555'};
`;

const Mono = styled.span`
   font-family: var(--hf-mono, 'JetBrains Mono', monospace);
   font-size: 0.8rem;
`;

const Muted = styled.span`
   color: ${({ theme }) => theme?.colors?.textMuted ?? '#999'};
   font-style: italic;
`;

const StatusBadge = styled.span`
   display: inline-block;
   padding: 2px 8px;
   border-radius: 999px;
   font-size: 0.7rem;
   font-weight: 700;
   background: ${({ $active }) =>
      $active ? 'rgba(22,163,74,0.12)' : 'rgba(0,0,0,0.06)'};
   color: ${({ $active }) => ($active ? '#15803d' : '#777')};
`;

const RowActions = styled.div`
   display: flex;
   gap: 6px;
   justify-content: flex-end;
`;

const EmptyRow = styled.div`
   padding: 24px;
   text-align: center;
   color: ${({ theme }) => theme?.colors?.textMuted ?? '#888'};
   font-size: 0.85rem;
`;

function formatBRL(value) {
   const numeric = Number(value);
   if (Number.isNaN(numeric)) return 'R$ 0,00';
   return numeric.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
   });
}

export default function ServiceCatalog() {
   const permissions = useSelector(
      (state) => state.auth.user?.permissions || []
   );
   const canRead = permissions.includes('routing:schedule_item:read');
   const canManage = permissions.includes('routing:service_catalog:manage');
   const canManageCommission = permissions.includes('finance:commission:manage');
   // Quem pode abrir a ficha de edição: logística (tudo menos comissão) ou
   // financeiro (só comissão). Cada um vê apenas os campos que pode editar.
   const canEdit = canManage || canManageCommission;

   const [items, setItems] = useState([]);
   const [includeInactive, setIncludeInactive] = useState(false);
   const [isLoading, setIsLoading] = useState(true);
   const [togglingId, setTogglingId] = useState(null);

   // form = { mode: 'create' | 'edit', target } | null
   const [form, setForm] = useState(null);

   const load = useCallback(async () => {
      setIsLoading(true);
      try {
         const list = await listServiceCatalog({ includeInactive });
         setItems(Array.isArray(list) ? list : []);
      } catch (err) {
         toast.error(
            err?.response?.data?.message ??
               'Não foi possível carregar o catálogo de serviços.'
         );
      } finally {
         setIsLoading(false);
      }
   }, [includeInactive]);

   useEffect(() => {
      if (canRead) load();
   }, [canRead, load]);

   // Salva a ficha. Logística e financeiro têm endpoints distintos — a ficha
   // devolve só os blocos que o operador pode (e quis) mudar; orquestramos as
   // chamadas aqui. Erros propagam pro modal mapear por campo.
   const handleSubmitForm = useCallback(
      async ({ catalogBody, commission }) => {
         if (form?.mode === 'create') {
            const result = await createServiceType(catalogBody);
            toast.success(result.message);
         } else {
            let lastMessage = null;
            if (catalogBody) {
               const result = await updateServiceType(form.target.id, catalogBody);
               lastMessage = result.message;
            }
            if (commission !== undefined) {
               const result = await updateServiceTypeCommission(
                  form.target.id,
                  commission
               );
               lastMessage = result.message;
            }
            if (lastMessage) toast.success(lastMessage);
         }
         setForm(null);
         await load();
      },
      [form, load]
   );

   const handleToggleActive = useCallback(
      async (service) => {
         setTogglingId(service.id);
         try {
            const result = await updateServiceType(service.id, {
               is_active: !service.is_active,
            });
            toast.success(result.message);
            await load();
         } catch (err) {
            toast.error(
               err?.response?.data?.message ??
                  'Não foi possível alterar o status do serviço.'
            );
         } finally {
            setTogglingId(null);
         }
      },
      [load]
   );

   if (!canRead) {
      return (
         <Container>
            <PageHeader>
               <PageHeaderText>
                  <PageTitle>Sem permissão</PageTitle>
                  <PageSubtitle>
                     Você não tem acesso ao catálogo de serviços.
                  </PageSubtitle>
               </PageHeaderText>
            </PageHeader>
         </Container>
      );
   }

   return (
      <Container>
         <Loading isLoading={isLoading} />

         <PageHeader>
            <PageHeaderText>
               <PageTitle>Catálogo de serviços</PageTitle>
               <PageSubtitle>
                  Fonte única dos serviços: tempo de execução (logística) e
                  comissão por unidade (financeiro). O tempo alimenta a duração
                  estimada dos itens; a comissão alimenta o fechamento.
               </PageSubtitle>
            </PageHeaderText>
            {canManage && (
               <PageHeaderActions>
                  <PrimaryButton
                     type="button"
                     onClick={() => setForm({ mode: 'create', target: null })}
                  >
                     Novo serviço
                  </PrimaryButton>
               </PageHeaderActions>
            )}
         </PageHeader>

         <Section>
            <SectionHeader>
               <SectionTitle>Serviços</SectionTitle>
               <SectionActions>
                  <InlineCheckbox>
                     <input
                        type="checkbox"
                        checked={includeInactive}
                        onChange={(e) => setIncludeInactive(e.target.checked)}
                     />
                     Mostrar inativos
                  </InlineCheckbox>
               </SectionActions>
            </SectionHeader>

            <SectionBody>
               <TableWrapper $minWidth="900px">
                  <Table>
                     <thead>
                        <tr>
                           <th>Código</th>
                           <th>Descrição</th>
                           <th>Tipo de produto</th>
                           <th>Categoria</th>
                           <th className="center">Tempo (min/un)</th>
                           <th className="center">Comissão (un)</th>
                           <th className="center">Status</th>
                           {canEdit && <th className="center">Ações</th>}
                        </tr>
                     </thead>
                     <tbody>
                        {items.map((svc) => (
                           <tr key={svc.id}>
                              <td>
                                 <Mono>{svc.service_code}</Mono>
                              </td>
                              <td>{svc.service_description}</td>
                              <td>{svc.product_type || <Muted>—</Muted>}</td>
                              <td>{svc.service_category || <Muted>—</Muted>}</td>
                              <td className="center">
                                 {svc.estimated_completion_service_time != null ? (
                                    `${svc.estimated_completion_service_time} min`
                                 ) : (
                                    <Muted>não definido</Muted>
                                 )}
                              </td>
                              <td className="center">
                                 {formatBRL(svc.commission_value_per_unit)}
                              </td>
                              <td className="center">
                                 <StatusBadge $active={Boolean(svc.is_active)}>
                                    {svc.is_active ? 'Ativo' : 'Inativo'}
                                 </StatusBadge>
                              </td>
                              {canEdit && (
                                 <td>
                                    <RowActions>
                                       <SmallButton
                                          type="button"
                                          onClick={() =>
                                             setForm({ mode: 'edit', target: svc })
                                          }
                                       >
                                          Editar
                                       </SmallButton>
                                       {canManage && (
                                          <SmallButton
                                             type="button"
                                             onClick={() => handleToggleActive(svc)}
                                             disabled={togglingId === svc.id}
                                          >
                                             {svc.is_active ? 'Desativar' : 'Ativar'}
                                          </SmallButton>
                                       )}
                                    </RowActions>
                                 </td>
                              )}
                           </tr>
                        ))}
                     </tbody>
                  </Table>
                  {!isLoading && items.length === 0 && (
                     <EmptyRow>
                        Nenhum serviço{' '}
                        {includeInactive ? 'cadastrado' : 'ativo'}.
                     </EmptyRow>
                  )}
               </TableWrapper>
            </SectionBody>
         </Section>

         {form && (
            <ServiceTypeFormModal
               mode={form.mode}
               serviceType={form.target}
               canManage={canManage}
               canManageCommission={canManageCommission}
               onClose={() => setForm(null)}
               onSubmit={handleSubmitForm}
            />
         )}
      </Container>
   );
}
